import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import { ScoreCircle } from '../../components/MatchBar';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
  Heart, X, MapPin, Briefcase, DollarSign,
  Filter, RefreshCw, CheckCircle, Loader2, Building2,
  Globe, Phone, Mail, ExternalLink, AlertTriangle, Info
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:3001';

// ── Modal perfil da empresa para candidatos ───────────────────
function ModalEmpresa({ vagaId, empresaId, nomeEmpresa, logoUrl, onFechar }) {
  const [empresa, setEmpresa] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!empresaId) { setLoading(false); return; }
    api.get(`/empresas/publica/${empresaId}`)
      .then(r => setEmpresa(r.data))
      .catch(() => setEmpresa({ nome_fantasia: nomeEmpresa, razao_social: nomeEmpresa }))
      .finally(() => setLoading(false));
  }, [empresaId, nomeEmpresa]);

  const nome = empresa?.nome_fantasia || empresa?.razao_social || nomeEmpresa;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={e => e.target === e.currentTarget && onFechar()}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] overflow-y-auto">

        {/* Header */}
        <div className="bg-gradient-to-r from-acre-dark to-[#1a3820] p-5 rounded-t-2xl flex items-start gap-4 relative">
          <button onClick={onFechar}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/80">
            <X size={14}/>
          </button>
          <div className="w-14 h-14 rounded-xl bg-white/10 overflow-hidden flex-shrink-0 flex items-center justify-center">
            {logoUrl
              ? <img src={logoUrl.startsWith('http') ? logoUrl : `${API_BASE}${logoUrl}`} alt={nome} className="w-full h-full object-contain p-1"/>
              : <Building2 size={26} className="text-white/60"/>}
          </div>
          <div className="pr-8">
            <h2 className="font-sora font-bold text-white text-lg leading-tight">{nome}</h2>
            {empresa?.setor && <p className="text-white/50 text-xs mt-0.5">{empresa.setor}</p>}
            {empresa?.vagas_abertas > 0 && (
              <span className="text-xs bg-acre-green/30 text-green-200 border border-acre-green/30 px-2 py-0.5 rounded-full font-semibold mt-1 inline-block">
                {empresa.vagas_abertas} vaga{empresa.vagas_abertas !== 1?'s':''} aberta{empresa.vagas_abertas!==1?'s':''}
              </span>
            )}
          </div>
        </div>

        {/* Conteúdo */}
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 size={24} className="animate-spin text-acre-green"/></div>
        ) : (
          <div className="p-5 space-y-4">
            {empresa?.descricao ? (
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase mb-1.5">Sobre a empresa</p>
                <p className="text-sm text-gray-600 leading-relaxed bg-gray-50 rounded-xl p-4">{empresa.descricao}</p>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-xl p-4 text-center">
                <Building2 size={20} className="text-gray-200 mx-auto mb-1"/>
                <p className="text-xs text-gray-400">Descrição não disponível</p>
              </div>
            )}

            {/* Contatos */}
            {(empresa?.cidade || empresa?.website || empresa?.email_comercial) && (
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase mb-2">Contato</p>
                <div className="space-y-2">
                  {empresa.cidade && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <MapPin size={13} className="text-gray-400"/>{empresa.cidade}, Acre
                    </div>
                  )}
                  {empresa.website && (
                    <a href={empresa.website.startsWith('http')?empresa.website:`https://${empresa.website}`}
                       target="_blank" rel="noreferrer"
                       className="flex items-center gap-2 text-sm text-acre-green hover:underline">
                      <Globe size={13}/>{empresa.website}<ExternalLink size={10}/>
                    </a>
                  )}
                  {empresa.email_comercial && (
                    <a href={`mailto:${empresa.email_comercial}`}
                       className="flex items-center gap-2 text-sm text-blue-600 hover:underline">
                      <Mail size={13}/>{empresa.email_comercial}
                    </a>
                  )}
                </div>
              </div>
            )}

            <Link to="/candidato/matches" onClick={onFechar}
              className="btn-primary w-full justify-center py-3 flex items-center gap-2 text-sm">
              💚 Ver minha compatibilidade com as vagas
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

function corScore(s) {
  if (s >= 80) return 'text-green-600 bg-green-50 border-green-200';
  if (s >= 60) return 'text-lime-600 bg-lime-50 border-lime-200';
  if (s >= 40) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
  return 'text-gray-500 bg-gray-50 border-gray-200';
}
function labelScore(s) {
  if (s >= 80) return 'Excelente';
  if (s >= 60) return 'Ótimo';
  if (s >= 40) return 'Bom';
  return 'Baixo';
}

export default function MatchesCandidato() {
  const [matches,      setMatches]      = useState([]);
  const [curtidas,     setCurtidas]     = useState({});
  const [empresaSel,   setEmpresaSel]   = useState(null); // { vagaId, empresaId, nome, logo }
  const [loading,      setLoading]      = useState(true);
  const [filtro,       setFiltro]       = useState('todos');
  const [minScore,     setMinScore]     = useState(0);
  const [enviando,     setEnviando]     = useState(null);
  const [notifVagas,   setNotifVagas]   = useState([]);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const [rMatches, rCurtidas, rNotif] = await Promise.all([
        api.get('/candidatos/matches'),
        api.get('/candidatos/vagas/curtidas').catch(() => ({ data: {} })),
        api.get('/candidatos/notificacoes').catch(() => ({ data: [] })),
      ]);
      setMatches(rMatches.data.matches || []);
      setCurtidas(rCurtidas.data || {});
      setNotifVagas((rNotif.data || []).filter(n => n.tipo === 'vaga_atualizada' && !n.lida));
    } catch {
      toast.error('Erro ao carregar vagas.');
    }
    setLoading(false);
  }, []);

  const marcarNotifLida = async (id) => {
    await api.post(`/candidatos/notificacoes/${id}/lida`).catch(() => {});
    setNotifVagas(p => p.filter(n => n.id !== id));
  };

  useEffect(() => { carregar(); }, [carregar]);

  const agir = async (vagaId, acao) => {
    if (enviando) return;
    setEnviando(vagaId + acao);
    try {
      await api.post(`/candidatos/vagas/${vagaId}/${acao === 'curtiu' ? 'curtir' : 'pular'}`);
      setCurtidas(p => ({ ...p, [vagaId]: acao }));
      if (acao === 'curtiu') toast.success('💚 Interesse registrado!');
    } catch {
      toast.error('Erro ao registrar.');
    }
    setEnviando(null);
  };

  const visiveis = matches
    .filter(m => m.score_total >= minScore)
    .filter(m => {
      if (filtro === 'curtidas')  return curtidas[m.vaga_id] === 'curtiu';
      if (filtro === 'pendentes') return !curtidas[m.vaga_id];
      return true;
    });

  const totalCurtidas = Object.values(curtidas).filter(a => a === 'curtiu').length;

  return (
    <Layout title="Vagas Para Mim" subtitle="Ordenadas por compatibilidade com seu perfil">

      {/* Modal perfil empresa */}
      {empresaSel && (
        <ModalEmpresa
          vagaId={empresaSel.vagaId}
          empresaId={empresaSel.empresaId}
          nomeEmpresa={empresaSel.nome}
          logoUrl={empresaSel.logo}
          onFechar={() => setEmpresaSel(null)}
        />
      )}

      {/* Banner */}
      <div className="card p-4 mb-5 bg-gradient-to-r from-acre-dark to-acre-green text-white">
        <div className="flex items-center gap-3">
          <span className="text-2xl">🧠</span>
          <div>
            <p className="font-sora font-bold text-sm">Algoritmo de Match AcreJob</p>
            <p className="text-white/70 text-xs mt-0.5">
              Área de interesse <strong className="text-white">60%</strong> +
              Proximidade geográfica <strong className="text-white">40%</strong>
            </p>
          </div>
          {totalCurtidas > 0 && (
            <div className="ml-auto text-right flex-shrink-0">
              <p className="font-sora font-bold text-acre-gold text-lg">{totalCurtidas}</p>
              <p className="text-white/60 text-xs">interesse(s)</p>
            </div>
          )}
        </div>
      </div>

      {/* Notificações de vagas atualizadas */}
      {notifVagas.length > 0 && (
        <div className="mb-5 space-y-2">
          <p className="text-xs font-bold text-yellow-700 flex items-center gap-1.5">
            ⚠️ {notifVagas.length} vaga{notifVagas.length > 1 ? 's' : ''} que você curtiu {notifVagas.length > 1 ? 'foram atualizadas' : 'foi atualizada'}
          </p>
          {notifVagas.map(n => (
            <div key={n.id}
              className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 flex items-start gap-2.5">
              <span className="text-lg flex-shrink-0">📢</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-yellow-800">{n.titulo}</p>
                {n.corpo && (
                  <div className="mt-1 space-y-0.5">
                    {n.corpo.split('\n').filter(Boolean).map((linha, i) => (
                      <p key={i} className="text-xs text-yellow-700">{linha.replace(/\*\*/g,'')}</p>
                    ))}
                  </div>
                )}
                <div className="flex items-center justify-between mt-1.5">
                  <span className="text-xs text-yellow-600">
                    {new Date(n.created_at).toLocaleString('pt-BR', {day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'})}
                  </span>
                  <button onClick={() => marcarNotifLida(n.id)}
                    className="text-xs text-yellow-600 hover:text-yellow-800 underline">
                    Marcar como lida
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filtros */}
      <div className="flex flex-wrap gap-2 mb-5 items-center">
        {[
          { v: 'todos',     l: 'Todas as vagas' },
          { v: 'pendentes', l: '⏳ Pendentes' },
          { v: 'curtidas',  l: '💚 Com interesse' },
        ].map(f => (
          <button key={f.v} onClick={() => setFiltro(f.v)}
            className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-all ${
              filtro === f.v
                ? 'bg-acre-green text-white border-acre-green'
                : 'bg-white text-gray-500 border-gray-200 hover:border-acre-green hover:text-acre-green'
            }`}>
            {f.l}
          </button>
        ))}

        <div className="flex items-center gap-1 ml-auto">
          <Filter size={12} className="text-gray-400" />
          {[0, 40, 60, 80].map(s => (
            <button key={s} onClick={() => setMinScore(s)}
              className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${
                minScore === s
                  ? 'bg-acre-green text-white border-acre-green'
                  : 'bg-white text-gray-400 border-gray-200 hover:border-acre-green'
              }`}>
              {s === 0 ? 'Todos' : `≥${s}%`}
            </button>
          ))}
        </div>

        <button onClick={carregar} title="Atualizar"
          className="p-2 rounded-full border border-gray-200 text-gray-400 hover:text-acre-green hover:border-acre-green transition-colors">
          <RefreshCw size={13} />
        </button>
      </div>

      {/* Conteúdo */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3 text-gray-400">
          <Loader2 size={32} className="animate-spin text-acre-green" />
          <p className="text-sm">Calculando compatibilidade...</p>
        </div>
      ) : visiveis.length === 0 ? (
        <div className="card p-12 text-center">
          <p className="text-5xl mb-3">🎯</p>
          <p className="text-gray-600 font-semibold mb-1">
            {filtro === 'curtidas' ? 'Você ainda não demonstrou interesse em nenhuma vaga.'
             : filtro === 'pendentes' ? 'Todas as vagas já foram avaliadas!'
             : matches.length === 0 ? 'Nenhuma vaga disponível no momento.'
             : `Nenhuma vaga com score ≥ ${minScore}%.`}
          </p>
          <p className="text-gray-400 text-sm">
            {matches.length === 0
              ? 'Complete seu perfil com áreas de interesse para ver vagas compatíveis.'
              : 'Tente reduzir o filtro de compatibilidade mínima.'}
          </p>
          {matches.length === 0 && (
            <Link to="/candidato/perfil" className="btn-primary inline-block mt-4 text-sm px-5 py-2">
              Completar perfil →
            </Link>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {visiveis.map((m, i) => {
            const acao = curtidas[m.vaga_id];
            const jaCurtiu = acao === 'curtiu';
            const jaPulou  = acao === 'pulou';
            const enviandoCurtir = enviando === m.vaga_id + 'curtiu';
            const enviandoPular  = enviando === m.vaga_id + 'pulou';

            return (
              <div key={m.vaga_id}
                className={`card p-4 flex items-center gap-3 transition-all ${
                  jaPulou  ? 'opacity-40 hover:opacity-70' :
                  jaCurtiu ? 'ring-2 ring-green-300 bg-green-50/30' :
                  'hover:shadow-md'
                }`}>

                {i < 3 && filtro === 'todos' && minScore === 0 && (
                  <span className="text-lg flex-shrink-0">{['🥇','🥈','🥉'][i]}</span>
                )}

                {/* Logo */}
                <div className="w-11 h-11 rounded-xl overflow-hidden border border-gray-100 bg-gray-50 flex items-center justify-center flex-shrink-0">
                  {m.logo_empresa
                    ? <img src={`${API_BASE}${m.logo_empresa}`} alt={m.empresa} className="w-full h-full object-contain p-1" />
                    : <Building2 size={20} className="text-gray-300" />}
                </div>

                {/* Score */}
                <ScoreCircle score={m.score_total} size="sm" />

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-2 flex-wrap">
                    <p className="font-sora font-bold text-sm text-acre-dark leading-tight truncate">{m.titulo}</p>
                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full border flex-shrink-0 ${corScore(m.score_total)}`}>
                      {labelScore(m.score_total)}
                    </span>
                  </div>
                  <button
                    onClick={() => setEmpresaSel({ vagaId: m.vaga_id, empresaId: m.empresa_id, nome: m.empresa, logo: m.logo_empresa })}
                    className="text-xs text-acre-green font-semibold truncate hover:underline flex items-center gap-1 text-left">
                    {m.empresa}
                    <Building2 size={10} className="flex-shrink-0 opacity-50"/>
                  </button>
                  <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-gray-400">
                    <span className="flex items-center gap-0.5"><MapPin size={10}/>{m.cidade_vaga}, AC</span>
                    {m.modalidade && <span className="flex items-center gap-0.5"><Briefcase size={10}/>{m.modalidade}</span>}
                    {m.salario_min && (
                      <span className="flex items-center gap-0.5 text-green-600 font-semibold">
                        <DollarSign size={10}/>R$ {Number(m.salario_min).toLocaleString('pt-BR')}
                      </span>
                    )}
                  </div>
                  {/* Barra de progresso */}
                  <div className="mt-1.5 flex items-center gap-2">
                    <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${
                        m.score_total >= 80 ? 'bg-green-500' :
                        m.score_total >= 60 ? 'bg-lime-500' :
                        m.score_total >= 40 ? 'bg-yellow-500' : 'bg-gray-300'
                      }`} style={{ width: `${m.score_total}%` }} />
                    </div>
                    <span className="text-xs font-bold text-gray-400 flex-shrink-0">{Math.round(m.score_total)}%</span>
                  </div>
                </div>

                {/* Botões */}
                <div className="flex gap-2 flex-shrink-0">
                  {jaCurtiu ? (
                    <div className="flex items-center gap-1 text-xs text-green-600 font-bold bg-green-50 border border-green-200 px-2.5 py-1.5 rounded-xl">
                      <CheckCircle size={13}/> <span className="hidden sm:inline">Interesse</span>
                    </div>
                  ) : jaPulou ? (
                    <div className="flex items-center gap-1 text-xs text-gray-400 bg-gray-50 border border-gray-200 px-2.5 py-1.5 rounded-xl">
                      <X size={13}/> <span className="hidden sm:inline">Pulou</span>
                    </div>
                  ) : (
                    <>
                      <button onClick={() => agir(m.vaga_id, 'pulou')} disabled={!!enviando}
                        title="Pular"
                        className="w-9 h-9 rounded-full border-2 border-gray-200 hover:border-red-400 hover:bg-red-50 flex items-center justify-center text-gray-300 hover:text-red-500 transition-all active:scale-90 disabled:opacity-40">
                        {enviandoPular ? <Loader2 size={14} className="animate-spin" /> : <X size={16} />}
                      </button>
                      <button onClick={() => agir(m.vaga_id, 'curtiu')} disabled={!!enviando}
                        title="Tenho interesse"
                        className="w-9 h-9 rounded-full border-2 border-gray-200 hover:border-green-400 hover:bg-green-50 flex items-center justify-center text-gray-300 hover:text-green-500 transition-all active:scale-90 disabled:opacity-40">
                        {enviandoCurtir ? <Loader2 size={14} className="animate-spin text-green-500" /> : <Heart size={16} />}
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {visiveis.length > 0 && (
        <p className="text-center text-xs text-gray-400 mt-6">
          {visiveis.length} vaga{visiveis.length !== 1 ? 's' : ''} ·{' '}
          <button onClick={() => { setFiltro('todos'); setMinScore(0); }}
            className="text-acre-green hover:underline">ver todas</button>
        </p>
      )}
    </Layout>
  );
}
